import {
  auditFacts,
  EntityHeader,
  type EntityHeaderFact,
  InlineEdit,
  UtcRange,
} from "@/shared/components/EntityPage";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { type SessionRecord } from "@/shared/domain/sessions";
import { useUpdateSession } from "@/shared/domain/sessions/hooks";
import { CalendarRange, Timer } from "@/shared/foundation/icons";
import { formatShortNanoDuration, nanoSecToDate } from "@/shared/time";

import { MarkSessionCompleteButton } from "./CompleteSessionDialog";
import { SessionStatusBadge } from "./SessionStatusBadge";

interface SessionHeaderProps {
  session: SessionRecord;
}

/**
 * The header of a session page: name with its status badge and Mark complete
 * (while it can be), then who created it and when, and when it was recorded
 * and for how long (once it has files), then the description and tags. The
 * ID is left to the breadcrumb. Editing is offered to everyone who can see
 * the session; the API refuses a change the viewer may not make.
 */
export function SessionHeader({ session }: SessionHeaderProps) {
  const notify = useNotification();
  const { mutateAsync: updateSession } = useUpdateSession();

  // The name and description report a failed save in place; a tag, here.
  const save = (updates: Parameters<typeof updateSession>[0]["updates"]) =>
    updateSession({ sessionId: session.session_id, updates });
  const update = (updates: Parameters<typeof save>[0]) =>
    void save(updates).catch(() => notify("Couldn't save the change", "error"));

  return (
    <EntityHeader
      type="session"
      name={
        <InlineEdit
          value={session.name ?? ""}
          onSave={(name) => save({ name })}
          required
          placeholder="Untitled session"
          textStyle="title"
          ariaLabel="session name"
        />
      }
      badges={<SessionStatusBadge session={session} />}
      actions={<MarkSessionCompleteButton session={session} />}
      description={{
        value: session.description,
        canEdit: true,
        onSave: (description) => save({ description }),
      }}
      tags={{
        value: session.tags,
        canAdd: true,
        onAdd: (tag) => update({ metadata_changeset: { put_tags: [tag] } }),
      }}
      meta={[
        ...auditFacts({
          created: session.created,
          createdBy: session.created_by,
        }),
        ...recordingFacts(session),
      ]}
    />
  );
}

/** When it was recorded and for how long, or nothing while it has no files. */
function recordingFacts(session: SessionRecord): EntityHeaderFact[] {
  const minNs = session.min_timestamp_ns;
  const maxNs = session.max_timestamp_ns;
  if (minNs === null || maxNs === null) {
    return [];
  }
  return [
    {
      key: "recorded",
      icon: <CalendarRange />,
      label: "Recorded",
      value: (
        <UtcRange start={nanoSecToDate(minNs)} end={nanoSecToDate(maxNs)} />
      ),
    },
    {
      key: "duration",
      icon: <Timer />,
      label: "Duration",
      value: formatShortNanoDuration(maxNs - minNs),
    },
  ];
}
